import { useState, useRef } from 'react';
import { Music, Link as LinkIcon, X, Loader2, ExternalLink, AlertCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GENRES = [
  'House', 'Tech House', 'Deep House', 'Techno', 'Trance', 
  'Drum & Bass', 'Dubstep', 'Hip Hop', 'R&B', 'Afrobeats',
  'Reggaeton', 'Dancehall', 'UK Garage', 'Amapiano', 'Other'
];

const PLATFORM_STYLES = {
  spotify: { name: 'Spotify', color: '#1DB954' },
  apple_music: { name: 'Apple Music', color: '#FA243C' },
  tidal: { name: 'TIDAL', color: '#00FFFF' },
  soundcloud: { name: 'SoundCloud', color: '#FF5500' },
  youtube: { name: 'YouTube', color: '#FF0000' }
};

export const TrackShareModal = ({ isOpen, onClose, onSubmit }) => {
  const [url, setUrl] = useState('');
  const [description, setDescription] = useState('');
  const [genre, setGenre] = useState('');
  const [bpm, setBpm] = useState('');
  const [loading, setLoading] = useState(false);
  const [metadata, setMetadata] = useState(null);
  const [error, setError] = useState('');
  const debounceRef = useRef(null);

  if (!isOpen) return null;

  const fetchMetadata = async (inputUrl) => {
    if (!inputUrl.trim()) {
      setMetadata(null);
      setError('');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await axios.get(`${API}/community/track-metadata`, {
        params: { url: inputUrl }
      });
      setMetadata(response.data);
    } catch (err) {
      setMetadata(null);
      setError(err.response?.data?.detail || 'Could not fetch track info');
    } finally {
      setLoading(false);
    }
  };

  const handleUrlChange = (value) => {
    setUrl(value);
    
    // Debounce the metadata fetch
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    
    debounceRef.current = setTimeout(() => {
      fetchMetadata(value);
    }, 800);
  };

  const handleSubmit = async () => {
    if (!url || !description) return;

    setLoading(true);
    try {
      await onSubmit({
        url,
        description,
        genre: genre || null,
        bpm: bpm ? parseInt(bpm) : null
      });
      
      // Reset form
      setUrl('');
      setDescription('');
      setGenre('');
      setBpm('');
      setMetadata(null);
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to share track');
    } finally {
      setLoading(false);
    }
  };

  const platform = metadata ? PLATFORM_STYLES[metadata.platform] : null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" onClick={onClose}>
      <div 
        className="rounded-2xl p-6 border max-w-lg w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        style={{ background: '#1a1a1a', border: '1px solid rgba(212, 175, 55, 0.3)' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold flex items-center gap-2">
            <Music size={24} style={{ color: '#D4AF37' }} />
            Share a Track
          </h3>
          <button onClick={onClose} className="p-1 hover:bg-white/10 rounded">
            <X size={20} />
          </button>
        </div>

        <div className="space-y-4">
          {/* URL Input */}
          <div>
            <Label>Streaming Link *</Label>
            <div className="relative mt-1">
              <LinkIcon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input
                value={url}
                onChange={(e) => handleUrlChange(e.target.value)}
                placeholder="Paste Spotify, Apple Music, TIDAL, SoundCloud, or YouTube link"
                className="pl-10"
              />
              {loading && (
                <Loader2 size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#D4AF37] animate-spin" />
              )}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Supported: Spotify, Apple Music, TIDAL, SoundCloud, YouTube
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/30">
              <AlertCircle size={16} className="text-red-500" />
              <span className="text-sm text-red-400">{error}</span>
            </div>
          )}

          {/* Track Preview */}
          {metadata && !error && (
            <div 
              className="rounded-xl p-4 flex gap-3"
              style={{ background: `${platform.color}10`, border: `1px solid ${platform.color}30` }}
            >
              {/* Artwork */}
              <div 
                className="w-16 h-16 rounded-lg flex-shrink-0 flex items-center justify-center overflow-hidden"
                style={{ background: 'rgba(0,0,0,0.3)' }}
              >
                {metadata.artwork ? (
                  <img src={metadata.artwork} alt={metadata.title} className="w-full h-full object-cover" />
                ) : (
                  <Music size={24} style={{ color: platform.color }} />
                )}
              </div>
              
              {/* Info */}
              <div className="flex-1 min-w-0">
                <h4 className="font-bold truncate">{metadata.title || 'Unknown Track'}</h4>
                <p className="text-sm text-gray-400 truncate">{metadata.artist || 'Unknown Artist'}</p>
                <span 
                  className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full mt-2"
                  style={{ background: `${platform.color}20`, color: platform.color }}
                >
                  <ExternalLink size={10} />
                  {platform.name}
                </span>
              </div>
            </div>
          )}

          {/* Description */}
          <div>
            <Label>Description *</Label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Why are you sharing this track? What makes it special?"
              rows={3}
              className="mt-1"
            />
          </div>

          {/* Genre & BPM Row */}
          <div className="flex gap-4">
            <div className="flex-1">
              <Label>Genre (optional)</Label>
              <select
                value={genre}
                onChange={(e) => setGenre(e.target.value)}
                className="w-full mt-1 px-3 py-2 rounded-lg bg-white/5 border border-gray-600 focus:border-[#D4AF37] focus:outline-none"
              >
                <option value="">Select genre</option>
                {GENRES.map((g) => (
                  <option key={g} value={g}>{g}</option>
                ))}
              </select>
            </div>
            
            <div className="w-24">
              <Label>BPM (optional)</Label>
              <Input
                type="number"
                value={bpm}
                onChange={(e) => setBpm(e.target.value)}
                placeholder="128"
                className="mt-1"
                min="60"
                max="200"
              />
            </div>
          </div>

          {/* Submit Button */}
          <Button
            onClick={handleSubmit}
            disabled={loading || !url || !description || !!error}
            className="w-full rounded-full mt-4"
            style={{ background: '#D4AF37', color: '#000' }}
          >
            {loading ? (
              <>
                <Loader2 size={16} className="mr-2 animate-spin" />
                Sharing...
              </>
            ) : (
              'Share Track'
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};
